import React, { useMemo, useState } from 'react';
import {
    Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form,
} from 'reactstrap';
import { faAngleRight, faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NSButton, NSMultiSelect } from 'bricks';
import { OptionType } from 'bricks/NSSelectWithCheckboxes/NSSelectWithCheckboxes';

/* eslint-disable no-unused-vars */
export enum DocumentFilterCategory {
    TYPES = 'types',
    FORMATS = 'formats',
}

interface Props {
  typeOptions: OptionType[];
  formatOptions: OptionType[];
  selectedFilters: Partial<Record<DocumentFilterCategory, any[]>>;
  onFiltersChange: (filters: Partial<Record<DocumentFilterCategory, any[]>>) => void;
}

export const DocumentFilters = ({
    typeOptions,
    formatOptions,
    selectedFilters,
    onFiltersChange,
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<{ label: string; value: DocumentFilterCategory } | null>(null);
    const [draftFilters, setDraftFilters] = useState(selectedFilters);

    const filterCategories = [
        { label: 'Type', value: DocumentFilterCategory.TYPES },
        { label: 'Format', value: DocumentFilterCategory.FORMATS },
    ];

    const options = useMemo(() => {
        if (selectedCategory?.value === DocumentFilterCategory.TYPES) return typeOptions;
        if (selectedCategory?.value === DocumentFilterCategory.FORMATS) return formatOptions;
        return [];
    }, [selectedCategory, typeOptions, formatOptions]);

    const toggle = () => {
        setIsOpen(!isOpen);
        setSelectedCategory(null);
    };

    const handleClickCategory = (category: { label: string; value: DocumentFilterCategory }) => {
        setSelectedCategory(category);
        setDraftFilters(prev => ({
            ...prev,
            [category.value]: selectedFilters[category.value] || [],
        }));
    };

    const handleMultiSelectChange = (values: any[], category: DocumentFilterCategory) => {
        setDraftFilters(prev => ({
            ...prev,
            [category]: values,
        }));
    };

    const handleApply = () => {
        onFiltersChange(draftFilters);
        setSelectedCategory(null);
        setIsOpen(false);
    };

    return (
        <Dropdown toggle={toggle} isOpen={isOpen}>
            <DropdownToggle tag="div" data-testid="doc-filter-dropdown-toggle">
                <NSButton
                    icon={faFilter}
                    text="Filter"
                    color="secondary"
                    outline={false}
                    callback={toggle}
                />
            </DropdownToggle>
            {!selectedCategory ? (
                <DropdownMenu className="text-white">
                    {filterCategories.map(category => (
                        <DropdownItem
                            key={category.value}
                            className="px-2 py-1 text-white d-flex justify-content-between align-items-center"
                            toggle={false}
                            onClick={() => handleClickCategory(category)}
                        >
                            <p className="mb-0 mr-4">{category.label}</p>
                            <FontAwesomeIcon icon={faAngleRight} />
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            ) : (
                <DropdownMenu className="text-white" style={{ minWidth: '300px' }}>
                    <Form className="px-3 pb-2">
                        <NSMultiSelect
                            options={options}
                            value={draftFilters[selectedCategory.value] || []}
                            selectName="filterOptions"
                            onChange={values => handleMultiSelectChange(values, selectedCategory.value)}
                            label={selectedCategory.label}
                            withSelectAllOption
                        />
                        <div className="mt-3 text-right">
                            <NSButton text="Cancel" outline={false} callback={toggle} />
                            <NSButton text="Apply" color="primary" outline={false} callback={handleApply} className="ml-1" />
                        </div>
                    </Form>
                </DropdownMenu>
            )}
        </Dropdown>
    );
};

export default DocumentFilters;
